import { request, unwrapApiData } from '@/lib/api';

/** R1 supported favorite target types — do not expand without product + authz design. */
export type FavoriteItemType = 'workspace' | 'project' | 'dashboard';

export const FAVORITE_ITEM_TYPES: readonly FavoriteItemType[] = [
  'workspace',
  'project',
  'dashboard',
] as const;

export function isFavoriteItemType(value: unknown): value is FavoriteItemType {
  return (
    typeof value === 'string' &&
    (FAVORITE_ITEM_TYPES as readonly string[]).includes(value)
  );
}

/**
 * Safe projection from GET/POST/PUT /favorites (backend SafeFavoriteResponse).
 * Prefer `favoriteId`, `title`, `href`, `parentLabel` for UI.
 * `id` / `displayName` retained as aliases for transitional callers.
 */
export interface Favorite {
  favoriteId: string;
  id: string;
  userId: string;
  organizationId: string;
  itemType: FavoriteItemType;
  itemId: string;
  title: string;
  displayName: string;
  parentLabel?: string;
  href: string;
  displayOrder: number;
  createdAt: string;
}

function normalizeFavorite(raw: unknown): Favorite | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const itemType = o.itemType;
  if (!isFavoriteItemType(itemType)) return null;
  const favoriteId =
    typeof o.favoriteId === 'string'
      ? o.favoriteId
      : typeof o.id === 'string'
        ? o.id
        : null;
  const itemId = typeof o.itemId === 'string' ? o.itemId : null;
  const title =
    typeof o.title === 'string'
      ? o.title
      : typeof o.displayName === 'string'
        ? o.displayName
        : null;
  const href = typeof o.href === 'string' ? o.href : null;
  if (!favoriteId || !itemId || !title || !href) return null;
  if (!href.startsWith('/') || href.startsWith('//')) return null;

  return {
    favoriteId,
    id: favoriteId,
    userId: typeof o.userId === 'string' ? o.userId : '',
    organizationId: typeof o.organizationId === 'string' ? o.organizationId : '',
    itemType,
    itemId,
    title,
    displayName: title,
    parentLabel: typeof o.parentLabel === 'string' ? o.parentLabel : undefined,
    href,
    displayOrder: typeof o.displayOrder === 'number' ? o.displayOrder : 0,
    createdAt: typeof o.createdAt === 'string' ? o.createdAt : '',
  };
}

export async function listFavorites(options?: {
  type?: FavoriteItemType;
  limit?: number;
}): Promise<Favorite[]> {
  const params = new URLSearchParams();
  if (options?.type) params.set('type', options.type);
  if (options?.limit != null) params.set('limit', String(options.limit));
  const qs = params.toString();
  const raw = await request.get<unknown>(qs ? `/favorites?${qs}` : '/favorites');
  const data = unwrapApiData<{ favorites?: unknown[] }>(raw);
  const list = Array.isArray(data?.favorites) ? data.favorites : [];
  return list
    .map(normalizeFavorite)
    .filter((f): f is Favorite => f != null)
    .sort((a, b) => a.displayOrder - b.displayOrder);
}

export async function addFavorite(
  itemType: FavoriteItemType,
  itemId: string,
): Promise<Favorite> {
  if (!isFavoriteItemType(itemType)) {
    throw new Error('Unsupported favorite type');
  }
  const raw = await request.post<unknown>('/favorites', {
    itemType,
    itemId,
  });
  const data = unwrapApiData<{ favorite?: unknown }>(raw);
  const favorite = normalizeFavorite(data?.favorite);
  if (!favorite) {
    throw new Error('Add favorite returned no safe favorite');
  }
  return favorite;
}

/** Preferred: DELETE /favorites/:favoriteId */
export async function removeFavoriteById(favoriteId: string): Promise<void> {
  await request.delete(`/favorites/${favoriteId}`);
}

/**
 * @deprecated Prefer removeFavoriteById. Kept only for transitional call sites
 * that still resolve by itemType+itemId after looking up the favorite row.
 */
export async function removeFavorite(
  itemType: FavoriteItemType,
  itemId: string,
): Promise<void> {
  await request.delete(`/favorites?itemType=${itemType}&itemId=${itemId}`);
}

/** Full-list reorder — favoriteIds must be the complete ordered set. */
export async function reorderFavorites(
  favoriteIds: string[],
): Promise<Favorite[]> {
  const raw = await request.put<unknown>('/favorites/order', { favoriteIds });
  const data = unwrapApiData<{ favorites?: unknown[] }>(raw);
  const list = Array.isArray(data?.favorites) ? data.favorites : [];
  return list
    .map(normalizeFavorite)
    .filter((f): f is Favorite => f != null)
    .sort((a, b) => a.displayOrder - b.displayOrder);
}

export function findFavoriteForItem(
  favorites: Favorite[] | undefined,
  itemType: FavoriteItemType,
  itemId: string,
): Favorite | undefined {
  return favorites?.find((f) => f.itemType === itemType && f.itemId === itemId);
}
